import { motion } from "framer-motion";
import { NOOR } from "@/constants/testIds/noor";
import { fadeUp, fadeUpSmall, trans } from "./motion";

const NOTES = [
  { label: "Nose", value: "Cooked agave, white pepper, citrus blossom, hint of jasmine." },
  { label: "Palate", value: "Silken entry. Green apple, lime zest, mineral salt, soft anise." },
  { label: "Finish", value: "Long, warm, faintly peppered. Earth and rose, lingering." },
];

const PAIRINGS = [
  { name: "Neat", body: "Served at 14°C in a crystal copita — the way it was meant to be met." },
  { name: "Citrus", body: "A whisper of grapefruit oil. No more, no less." },
  { name: "On the rocks", body: "One large hand-cut stone. Twenty minutes of patience." },
];

function Note({ label, value, index }) {
  return (
    <motion.div
      {...fadeUp}
      transition={trans(0.7, index * 0.15)}
      className="col-span-12 md:col-span-4 border-t pt-8"
      style={{ borderColor: "var(--border-subtle)" }}
    >
      <p className="overline mb-5">{label}</p>
      <p className="font-display text-2xl md:text-3xl leading-snug" style={{ color: "var(--text-primary)" }}>
        {value}
      </p>
    </motion.div>
  );
}

function Pairing({ name, body, index }) {
  return (
    <motion.div
      {...fadeUpSmall}
      transition={trans(0.6, index * 0.1)}
      className="border-l pl-6 py-3"
      style={{ borderColor: "var(--accent-rose)" }}
    >
      <p className="font-display text-xl mb-2" style={{ color: "var(--accent-rose)" }}>{name}</p>
      <p className="text-sm font-light leading-relaxed" style={{ color: "var(--text-secondary)" }}>{body}</p>
    </motion.div>
  );
}

export default function Tasting() {
  return (
    <section data-testid={NOOR.tastingSection} id="tasting" className="relative py-32 md:py-44">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-y-10 md:gap-10 mb-16">
          <div className="col-span-12 lg:col-span-3">
            <p className="overline mb-4">III · Tasting</p>
            <span className="ornament" />
          </div>
          <div className="col-span-12 lg:col-span-7 lg:col-start-5">
            <motion.h2
              {...fadeUp}
              transition={trans(1)}
              className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]"
              style={{ color: "var(--text-primary)" }}
            >
              A flame that <em className="italic" style={{ color: "var(--accent-rose)" }}>doesn&rsquo;t burn</em> —<br />
              it illuminates.
            </motion.h2>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-y-8 md:gap-8">
          {NOTES.map((n, i) => <Note key={n.label} {...n} index={i} />)}
        </div>

        <div className="mt-28 grid grid-cols-12 gap-y-10 md:gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="overline mb-4">Serving rituals</p>
            <h3 className="font-display text-3xl md:text-4xl leading-tight" style={{ color: "var(--text-primary)" }}>
              Three ways<br />
              to meet the <em className="italic" style={{ color: "var(--accent-rose)" }}>light</em>.
            </h3>
          </div>
          <div className="col-span-12 lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-8">
            {PAIRINGS.map((p, i) => <Pairing key={p.name} {...p} index={i} />)}
          </div>
        </div>
      </div>
    </section>
  );
}
